import React, { useState } from 'react';
import { COMPETITIONS, MARKET_TYPES } from '../../data/mockData';
import { useBetting } from '../../context/BettingContext';
import HelpTip from '../shared/HelpTip';

export default function BettingCard({ market }) {
  const { placeBet, replaceBet, cancelBet, bets, settings, bettingUser, isMyBet, isTeamBased, participant, members = [] } = useBetting();
  const user = bettingUser;
  const [selectedOption, setSelectedOption] = useState(null);
  const [amount, setAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingBet, setEditingBet] = useState(null);

  // In a team activity each member has their own bet per market, paid from the team wallet.
  const isOwnBet = (b) => isMyBet(b) && (!participant?.teamId || b.userId === participant.memberId);
  const existingBet = bets.find((b) => isOwnBet(b) && b.marketId === market.id && b.status === 'pending');
  const teammateBets = participant?.teamId
    ? bets.filter((b) => isMyBet(b) && !isOwnBet(b) && b.marketId === market.id)
    : [];

  const typeInfo = MARKET_TYPES[Object.keys(MARKET_TYPES).find(
    (k) => MARKET_TYPES[k].id === market.type
  )];

  const comp = COMPETITIONS[Object.keys(COMPETITIONS).find(
    (k) => COMPETITIONS[k].id === market.competition
  )];

  const potentialWin = selectedOption && amount
    ? Math.round(parseFloat(amount) * selectedOption.odds * 100) / 100
    : 0;

  const handlePlaceBet = async () => {
    if (!selectedOption || !amount) return;
    if (isTeamBased && !participant) {
      setError('Ask an event admin to assign you to a team. Personal balances cannot be used in a team activity.');
      return;
    }
    const betAmount = parseFloat(amount);
    if (isNaN(betAmount) || betAmount <= 0) {
      setError('Enter a valid amount');
      return;
    }

    setPlacing(true);
    setError('');
    try {
      if (editingBet) {
        // Replacing an existing bet
        await replaceBet(editingBet.id, selectedOption.name, betAmount);
        setSuccess(`Bet updated! ${selectedOption.name} for ${betAmount} coins`);
        setEditingBet(null);
      } else {
        if (betAmount > user.balance) {
          setError(isTeamBased ? 'Insufficient team balance' : 'Insufficient balance');
          setPlacing(false);
          return;
        }
        await placeBet(market.id, selectedOption.name, betAmount);
        setSuccess(`Bet placed! ${selectedOption.name} for ${betAmount} coins`);
      }
      setSelectedOption(null);
      setAmount('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setPlacing(false);
    }
  };

  const handleEditBet = () => {
    if (!existingBet) return;
    setEditingBet(existingBet);
    const opt = market.options.find((o) => o.name === existingBet.optionName);
    setSelectedOption(opt || null);
    setAmount(String(existingBet.amount));
    setError('');
    setSuccess('');
  };

  const handleCancelBet = async () => {
    if (!existingBet) return;
    setPlacing(true);
    setError('');
    try {
      await cancelBet(existingBet.id);
      setSuccess('Bet cancelled and refunded!');
      setEditingBet(null);
      setSelectedOption(null);
      setAmount('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setPlacing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingBet(null);
    setSelectedOption(null);
    setAmount('');
    setError('');
  };

  const maxAmount = editingBet
    ? user.balance + editingBet.amount
    : user?.balance || 0;

  const quickAmounts = [10, 25, 50, 100, 250];
  const canReplace = settings.allowBetReplacement && market.status === 'open';
  const isEditing = !!editingBet;

  return (
    <div className={`bet-card ${market.status !== 'open' ? 'bet-card--closed' : ''}`}>
      <div className="bet-card-header">
        <div className="bet-card-comp" style={{ borderColor: comp?.color, color: comp?.color }}>
          {comp?.emoji} {comp?.name}
        </div>
        <span className="bet-card-type">
          {typeInfo?.icon} {typeInfo?.name}
        </span>
      </div>

      <h3 className="bet-card-match">{market.matchName}</h3>
      {market.matchDate ? <p className="bet-card-date">
        {new Date(market.matchDate).toLocaleDateString('en-GB', {
          weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
        })}
      </p> : <p className="bet-card-date">Open until closed by an admin</p>}

      {/* Existing bet indicator */}
      {existingBet && !isEditing && (
        <div className="bet-card-existing">
          <div className="bet-card-existing-header">
            <span className="bet-card-existing-label">Your Bet{canReplace && <HelpTip label="Changing a bet">While the market is open you can change or cancel your bet. Cancelling refunds the full stake{isTeamBased ? ' to your team wallet' : ''}.</HelpTip>}</span>
            {canReplace && (
              <div className="bet-card-existing-actions">
                <button className="bet-card-edit-btn" onClick={handleEditBet}>Change</button>
                <button className="bet-card-cancel-btn" onClick={handleCancelBet} disabled={placing}>Cancel</button>
              </div>
            )}
          </div>
          <div className="bet-card-existing-details">
            <span className="bet-card-existing-pick">{existingBet.optionName}</span>
            <span className="bet-card-existing-amount">{existingBet.amount} coins @ {existingBet.odds}</span>
            <span className="bet-card-existing-potential">→ {existingBet.potentialWin} coins</span>
          </div>
          {market.status === 'closed' && (
            <div className="bet-card-existing-locked">Betting is locked for this market</div>
          )}
        </div>
      )}

      {teammateBets.length > 0 && (
        <div className="bet-card-teammates">
          {teammateBets.map((b) => (
            <div key={b.id}>👥 {members.find((m) => m.id === b.userId)?.displayName || 'A teammate'}: <strong>{b.optionName}</strong> · {b.amount} coins @ {b.odds}</div>
          ))}
        </div>
      )}

      <div className="bet-card-options">
        {market.options.map((opt) => (
          <button
            key={opt.name}
            className={`bet-card-option ${selectedOption?.name === opt.name ? 'bet-card-option--selected' : ''} ${existingBet && !isEditing && existingBet.optionName === opt.name ? 'bet-card-option--has-bet' : ''}`}
            onClick={() => {
              if (market.status !== 'open') return;
              if (existingBet && !isEditing) return; // must click "Change" first
              setSelectedOption(selectedOption?.name === opt.name ? null : opt);
              setError('');
              setSuccess('');
            }}
            disabled={market.status !== 'open' || (existingBet && !isEditing)}
          >
            <span className="bet-card-option-name">{opt.name}</span>
            <span className="bet-card-option-odds">{opt.odds}</span>
          </button>
        ))}
      </div>

      {((selectedOption && market.status === 'open' && !existingBet) || isEditing) && (
        <div className="bet-card-slip">
          <div className="bet-card-slip-header">
            <span>{isEditing ? 'Updating:' : 'Your pick:'} <strong>{selectedOption?.name}</strong></span>
            <span className="bet-card-slip-odds">
              @ {selectedOption?.odds}
              <HelpTip label="Odds">Your stake is multiplied by the odds if this pick wins. A 100-coin stake at 2.5 pays out 250 coins (150 profit). If it loses, the stake is gone.</HelpTip>
            </span>
          </div>

          <div className="bet-card-slip-input">
            <input
              type="number"
              placeholder="Amount"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setError(''); }}
              min="1"
              max={maxAmount}
            />
            <div className="bet-card-quick-amounts">
              {quickAmounts.map((qa) => (
                <button
                  key={qa}
                  className="bet-card-quick-btn"
                  onClick={() => setAmount(String(Math.min(qa, maxAmount)))}
                >
                  {qa}
                </button>
              ))}
            </div>
          </div>

          {isTeamBased && participant && (
            <div className="bet-card-team-wallet">
              Paid from the {participant.name} wallet · {maxAmount.toLocaleString()} coins available
              <HelpTip label="Team wallet">Everyone in your team bets from one shared wallet. Your stake is taken from it immediately, and winnings are paid back into it when the market is settled.</HelpTip>
            </div>
          )}

          {potentialWin > 0 && (
            <div className="bet-card-potential">
              <span>Potential Win<HelpTip label="Potential win">The total paid out if this pick wins: stake × odds, including your stake.</HelpTip></span>
              <span className="bet-card-potential-value">{potentialWin.toLocaleString()} coins</span>
            </div>
          )}

          {error && <div className="bet-card-error">{error}</div>}
          {success && <div className="bet-card-success">{success}</div>}

          <div className="bet-card-slip-buttons">
            <button
              className="bet-card-place-btn"
              onClick={handlePlaceBet}
              disabled={placing || !amount || !selectedOption}
            >
              {placing ? 'Processing...' : isEditing ? `Update Bet - ${amount || 0} coins` : `Place Bet - ${amount || 0} coins`}
            </button>
            {isEditing && (
              <button className="bet-card-cancel-edit-btn" onClick={handleCancelEdit}>
                Cancel Edit
              </button>
            )}
          </div>
        </div>
      )}

      {success && !selectedOption && !isEditing && (
        <div className="bet-card-success" style={{ marginTop: 12 }}>{success}</div>
      )}

      {market.status === 'closed' && !existingBet && (
        <div className="bet-card-closed-badge">
          🔒 Betting Closed
        </div>
      )}

      {market.status === 'settled' && (
        <div className="bet-card-closed-badge">
          ✅ Settled {market.winningOption ? `- ${market.winningOption}` : ''}
        </div>
      )}
    </div>
  );
}
